import { auth } from "@/lib/firebase"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"

// Define the interface for a CME Report (matches backend Pydantic model)
export interface CMEReport {
	id: string
	case_id: string
	status: string
	created_at: string
	updated_at: string
	docx_filename?: string | null
	pdf_filename?: string | null
}

const backendUrl = import.meta.env.VITE_API_URL

// Hook to fetch all CME reports for a specific case
export const useCMEReportsForCase = (caseId: string) => {
	return useQuery<CMEReport[]>({
		queryKey: ["cmeReports", caseId], // Unique query key
		queryFn: async () => {
			const user = auth.currentUser
			if (!user) {
				throw new Error("User not found.")
			}
			const token = await user.getIdToken()
			const response = await axios.get(
				`${backendUrl}/api/cases/${caseId}/cme-reports`,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			)
			return response.data
		},
		enabled: !!caseId, // Only run query if caseId is available
	})
}

// Hook to trigger the generation of a new CME report
export const useGenerateCMEReport = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (caseId: string) => {
			const user = auth.currentUser
			if (!user) {
				throw new Error("User not found.")
			}
			const token = await user.getIdToken()
			const response = await axios.post(
				`${backendUrl}/api/cases/${caseId}/cme-reports`,
				{}, // Empty object as the body
				{ headers: { Authorization: `Bearer ${token}` } }
			)
			return response.data
		},
		onSuccess: (_, caseId) => {
			// Invalidate the query cache for cmeReports to refetch the list
			queryClient.invalidateQueries({ queryKey: ["cmeReports", caseId] })
		},
	})
}

// Hook to delete a specific CME report
export const useDeleteCMEReport = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async ({
			caseId,
			reportId,
		}: {
			caseId: string
			reportId: string
		}) => {
			const user = auth.currentUser
			if (!user) {
				throw new Error("User not found.")
			}
			const token = await user.getIdToken()
			await axios.delete(
				`${backendUrl}/api/cases/${caseId}/cme-reports/${reportId}`,
				{ headers: { Authorization: `Bearer ${token}` } }
			)
			return { caseId, reportId } // Return identifiers for onSuccess
		},
		onSuccess: ({ caseId }) => {
			// Invalidate the query cache for cmeReports
			queryClient.invalidateQueries({ queryKey: ["cmeReports", caseId] })
		},
	})
}

// Hook to download a specific CME report (PDF or DOCX)
export const useDownloadCMEReport = () => {
	return useMutation({
		mutationFn: async ({
			caseId,
			reportId,
			format = "pdf",
		}: {
			caseId: string
			reportId: string
			format?: "pdf" | "docx"
		}) => {
			const user = auth.currentUser
			if (!user) {
				throw new Error("User not found.")
			}
			const token = await user.getIdToken()
			const response = await axios.get(
				`${backendUrl}/api/cases/${caseId}/cme-reports/${reportId}`,
				{
					params: { format }, // Add format parameter
					responseType: "blob", // Get response as a Blob
					headers: { Authorization: `Bearer ${token}` },
				}
			)

			// Create a URL for the blob and trigger download
			const url = window.URL.createObjectURL(new Blob([response.data]))
			const link = document.createElement("a")
			link.href = url
			// Suggest a filename for the download
			link.setAttribute(
				"download",
				`case_${caseId}_cme_report_${reportId.substring(0, 8)}.${format}`
			)
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url) // Clean up the object URL

			return { caseId, reportId }
		},
		// No onSuccess invalidation needed as download doesn't change server state
	})
}

// Function to get the direct URL for embedding/linking to a CME report PDF
export const useGetCMEReportPdfUrl = (caseId: string, reportId: string) => {
	return `${backendUrl}/api/cases/${caseId}/cme-reports/${reportId}?format=pdf`
}
